import React, { useState, useEffect } from "react";
import { Headline, Paragraph, Section } from "./ui";
import { groupPorscheData } from "../utils/csvParser";

const AccordionItem = ({ title, children, level = 1, isOpen, onToggle }) => {
  const HeadingComponent = level === 1 ? "h3" : level === 2 ? "h4" : "h5";
  const headlineVariant = level === 1 ? "h3" : level === 2 ? "h3" : "h5";
  const accordionRef = React.useRef(null);

  const handleToggle = () => {
    onToggle();
    // Scroll the clicked element to the top of the page
    if (accordionRef.current) {
      setTimeout(() => {
        accordionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100); // Small delay to allow state update
    }
  };

  return (
    <div
      ref={accordionRef}
      className="border-b transition-colors"
      style={{ borderColor: "var(--color-secondary)" }}
    >
      <button
        onClick={handleToggle}
        className={`w-full py-4 ${
          isOpen ? "px-4" : "px-0"
        } flex items-center justify-between hover:opacity-90 transition-opacity`}
        style={{
          backgroundColor: isOpen
            ? "var(--color-secondary)"
            : "var(--color-primary)",
          color: isOpen ? "var(--color-primary)" : "var(--color-secondary)",
        }}
      >
        <Headline
          as={HeadingComponent}
          variant={headlineVariant}
          color={isOpen ? "primary" : "secondary"}
          className="text-left font-medium"
          noSpacing
        >
          {title}
        </Headline>
        <span
          className="text-2xl transition-transform duration-200"
          style={{
            color: isOpen ? "var(--color-primary)" : "var(--color-secondary)",
            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
          }}
        >
          +
        </span>
      </button>

      {isOpen && (
        <div
          className="pb-6"
          style={{ backgroundColor: "var(--color-primary)" }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const PricingRow = ({ variant }) => {
  return (
    <div
      className="grid grid-cols-8 lg:grid-cols-10 gap-4 py-3  hover:opacity-90 transition-opacity border-b"
      style={{
        backgroundColor: "var(--color-primary)",
        borderColor: "var(--color-secondary)",
        opacity: 0.95,
      }}
    >
      {/* Variant Name */}
      <div className="col-span-4">
        <Paragraph variant="medium" color="secondary" className="font-medium">
          {variant.name}
        </Paragraph>
        <Paragraph variant="small" color="secondary" className="opacity-75">
          <span className="hidden sm:inline">
            {variant.years} • {variant.engine} • {variant.power}
          </span>
          <span className="sm:hidden">{variant.years}</span>
        </Paragraph>
      </div>

      {/* Kleine Inspektion */}
      <div className="col-span-2">
        <Paragraph variant="medium" color="secondary" className="font-medium">
          ab €{variant.pricing.kleine.min}
        </Paragraph>
      </div>

      {/* Große Inspektion */}
      <div className="col-span-2">
        <Paragraph variant="medium" color="secondary" className="font-medium">
          ab €{variant.pricing.grosse.min}
        </Paragraph>
      </div>

      {/* Notes - Hidden on screens smaller than 1000px (lg breakpoint) */}
      <div className="col-span-2 hidden lg:block">
        {variant.notes && variant.notes.length > 0 && (
          <Paragraph variant="small" color="secondary" className="opacity-75">
            {variant.notes[0]}
          </Paragraph>
        )}
      </div>
    </div>
  );
};

const TableHeader = () => (
  <div
    className="grid grid-cols-8 lg:grid-cols-10 gap-4 py-2 border-b-2"
    style={{
      backgroundColor: "var(--color-primary)",
      borderColor: "var(--color-secondary)",
    }}
  >
    <div className="col-span-4">
      <Paragraph variant="medium" color="secondary" className="font-semibold">
        Modell
      </Paragraph>
    </div>
    <div className="col-span-2">
      <Paragraph variant="medium" color="secondary" className="font-semibold">
        <span className="hidden md:inline">Kleine Inspektion</span>
        <span className="md:hidden">Kleine Insp.</span>
      </Paragraph>
    </div>
    <div className="col-span-2">
      <Paragraph variant="medium" color="secondary" className="font-semibold">
        <span className="hidden md:inline">Große Inspektion</span>
        <span className="md:hidden">Große Insp.</span>
      </Paragraph>
    </div>
    <div className="col-span-2 hidden lg:block">
      <Paragraph variant="medium" color="secondary" className="font-semibold">
        Besonderheiten
      </Paragraph>
    </div>
  </div>
);

const InspectionPricingTable = () => {
  const [openModels, setOpenModels] = useState({});
  const [openGenerations, setOpenGenerations] = useState({});
  const [porscheServiceData, setPorscheServiceData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load data from CSV file (asynchronous)
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await groupPorscheData();
        setPorscheServiceData(data);
        setError(null);
      } catch (error) {
        console.error("Error loading pricing data:", error);
        setError(error.message);
        setPorscheServiceData({});
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const toggleModel = (modelKey) => {
    setOpenModels((prev) => {
      const isCurrentlyOpen = prev[modelKey];
      if (isCurrentlyOpen) {
        // If closing this model, also close all its generations
        const newGenerations = { ...openGenerations };
        Object.keys(newGenerations).forEach((key) => {
          if (key.startsWith(`${modelKey}-`)) {
            delete newGenerations[key];
          }
        });
        setOpenGenerations(newGenerations);
        return {
          ...prev,
          [modelKey]: false,
        };
      } else {
        // Close all other models and open this one
        // Also clear all generation states for this model to ensure fresh start
        const newGenerations = { ...openGenerations };
        Object.keys(newGenerations).forEach((key) => {
          if (key.startsWith(`${modelKey}-`)) {
            delete newGenerations[key];
          }
        });
        setOpenGenerations(newGenerations);
        return {
          [modelKey]: true,
        };
      }
    });
  };

  const toggleGeneration = (modelKey, genKey) => {
    const key = `${modelKey}-${genKey}`;
    setOpenGenerations((prev) => {
      const isCurrentlyOpen = prev[key];
      if (isCurrentlyOpen) {
        // Just close this generation
        const newState = { ...prev };
        delete newState[key];
        return newState;
      } else {
        // Close all other generations for this model and open this one
        const newState = {};
        Object.keys(prev).forEach((existingKey) => {
          if (!existingKey.startsWith(`${modelKey}-`)) {
            newState[existingKey] = prev[existingKey];
          }
        });
        newState[key] = true;
        return newState;
      }
    });
  };

  // Function to sort generations by start year
  const sortGenerationsByYear = (generations) => {
    return Object.entries(generations).sort(([, genA], [, genB]) => {
      // Extract start year from years string (e.g., "1964-1973" -> 1964)
      const getStartYear = (yearsString) => {
        if (!yearsString) return 0;
        const match = yearsString.match(/(\d{4})/);
        return match ? parseInt(match[1]) : 0;
      };

      const yearA = getStartYear(genA.years);
      const yearB = getStartYear(genB.years);

      return yearA - yearB;
    });
  };

  if (isLoading) {
    return (
      <Section variant="primary" spacing="tight" margin={false}>
        <div style={{ backgroundColor: "var(--color-primary)" }}>
          <Paragraph
            variant="medium"
            color="secondary"
            className="text-center py-8"
          >
            Lade Preisdaten...
          </Paragraph>
        </div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section variant="primary" spacing="tight" margin={false}>
        <div style={{ backgroundColor: "var(--color-primary)" }}>
          <Paragraph
            variant="medium"
            color="secondary"
            className="text-center py-8"
          >
            Fehler beim Laden der Preisdaten: {error}
          </Paragraph>
        </div>
      </Section>
    );
  }

  if (!porscheServiceData || Object.keys(porscheServiceData).length === 0) {
    return (
      <Section variant="primary" spacing="tight" margin={false}>
        <div style={{ backgroundColor: "var(--color-primary)" }}>
          <Paragraph
            variant="medium"
            color="secondary"
            className="text-center py-8"
          >
            Keine Preisdaten verfügbar.
          </Paragraph>
        </div>
      </Section>
    );
  }

  return (
    <Section variant="primary" spacing="tight" margin={false}>
      {/* Headline */}
      {/* Accordion Content */}
      <div style={{ backgroundColor: "var(--color-primary)" }}>
        {Object.entries(porscheServiceData).map(([modelKey, model]) => (
          <div key={modelKey}>
            {/* Model Level */}
            <AccordionItem
              title={`${model.displayName} (${
                Object.keys(model.generations).length
              })`}
              level={1}
              isOpen={openModels[modelKey]}
              onToggle={() => toggleModel(modelKey)}
            >
              {/* Generation Level */}
              <div>
                {sortGenerationsByYear(model.generations).map(
                  ([genKey, generation]) => (
                    <AccordionItem
                      key={genKey}
                      title={
                        <>
                          <span className="font-medium">{generation.name}</span>
                          <span className="font-normal opacity-75">
                            {" • "}
                            {generation.years} (
                            {Object.keys(generation.variants).length})
                          </span>
                        </>
                      }
                      level={2}
                      isOpen={openGenerations[`${modelKey}-${genKey}`]}
                      onToggle={() => toggleGeneration(modelKey, genKey)}
                    >
                      {/* Variants Table */}
                      <div>
                        {/* Table Header - only shown at variant level */}
                        <TableHeader />

                        {Object.entries(generation.variants).map(
                          ([varKey, variant]) => (
                            <PricingRow key={varKey} variant={variant} />
                          )
                        )}
                      </div>
                    </AccordionItem>
                  )
                )}
              </div>
            </AccordionItem>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div
        className="mt-12 p-4 rounded-lg border"
        style={{
          backgroundColor: "var(--color-primary)",
          borderColor: "var(--color-secondary)",
          opacity: 0.95,
        }}
      >
        <Paragraph
          variant="small"
          color="secondary"
          className="opacity-75 mt-4"
        >
          <strong>Hinweise:</strong> Alle Preise sind Richtwerte inkl.
          Mehrwertsteuer und Material. Die Preise können je nach
          Fahrzeugzustand, Zusatzarbeiten und Teileverfügbarkeit variieren. Wir
          erstellen Ihnen gerne einen Kostenvoranschlag vor Arbeitsbeginn.
        </Paragraph>
      </div>
    </Section>
  );
};

export default InspectionPricingTable;
